import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/Avatar";
import SendFeedback from "@/components/SendFeedback";
import GamesMenu from "@/components/GamesMenu";
import SettingsDialog from "@/components/SettingsDialog";
import Leaderboard from "@/components/Leaderboard";
import { useFriendBadge } from "@/lib/friends";
import { useGamesBadge } from "@/lib/games";
import { JangleBrand } from "@/components/JangleLogo";
import { Sun, Moon, SignOut, User, Eye, GameController, House, PuzzlePiece, ChatCircleText, Tray, ChartBar, UsersThree, ChatCircleDots, Bell, GearSix, ShieldCheck, GraduationCap } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const friendBadge = useFriendBadge(!!user);
  const friendCount = friendBadge.unread + friendBadge.requests;
  const gamesBadge = useGamesBadge(!!user);
  const gamesCount = gamesBadge.invites + gamesBadge.yourTurn + gamesBadge.gameOver;
  const gamesLabel = [
    gamesBadge.invites && `${gamesBadge.invites} game invite${gamesBadge.invites > 1 ? "s" : ""}`,
    gamesBadge.yourTurn && `your move in ${gamesBadge.yourTurn} game${gamesBadge.yourTurn > 1 ? "s" : ""}`,
    gamesBadge.gameOver && `${gamesBadge.gameOver} game${gamesBadge.gameOver > 1 ? "s" : ""} over`,
  ].filter(Boolean).join(", ");
  // One number on the profile picture: game invites + your-move games, plus
  // friend messages/requests (the desktop nav also shows those on Profile).
  const avatarCount = gamesCount + friendCount;

  // The count also shows in the browser tab, e.g. "(2) Jangle", so a player
  // with the site open in another tab can see an invite or message arrived.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\) /, "");
    document.title = avatarCount > 0 ? `(${avatarCount > 9 ? "9+" : avatarCount}) ${base}` : base;
  }, [avatarCount]);
  const countDot = (cls = "") =>
    friendCount > 0 ? (
      <span
        className={`min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-[1.1rem] text-center ${cls}`}
        data-testid="friends-badge"
      >
        {friendCount > 9 ? "9+" : friendCount}
      </span>
    ) : null;

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-tight transition-colors duration-200 ${
      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <header className="sticky top-0 z-40 glass-panel" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Brand and the right-hand side share the spare width equally, so
            the leaderboard trophy sits halfway between How to play and the
            theme toggle (Jack, 2026-10-15). */}
        <div className="flex-auto flex">
          <Link to={user ? "/lobby" : "/"} className="flex items-center gap-2.5" data-testid="brand-link">
            <JangleBrand logoClassName="h-9 md:h-10" />
          </Link>
        </div>

        {user && (
          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/lobby" className={linkCls} data-testid="nav-lobby">
              Lobby
            </NavLink>
            <NavLink to="/daily/archive" className={linkCls} data-testid="nav-daily">
              Daily puzzle
            </NavLink>
            <NavLink to="/watch" className={linkCls} data-testid="nav-watch">
              Watch
            </NavLink>
            <NavLink to="/profile" className={(a) => `${linkCls(a)} inline-flex items-center gap-1.5`} data-testid="nav-profile">
              Profile
              {countDot()}
            </NavLink>
            <NavLink to="/how-to-play" className={linkCls} data-testid="nav-how-to-play">
              How to play
            </NavLink>
          </nav>
        )}

        {/* Mobile "How to play" — still discoverable when the nav is collapsed. */}
        {user && (
          <NavLink
            to="/how-to-play"
            className="md:hidden text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="nav-how-to-play-mobile"
          >
            How to play
          </NavLink>
        )}

        <div className="flex-auto flex items-center">
        <div className="flex-1 flex justify-center">{user && <Leaderboard />}</div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="rounded-full"
            aria-label="Toggle theme"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun weight="fill" size={18} /> : <Moon weight="fill" size={18} />}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="rounded-full gap-2 pl-2 pr-3 h-9"
                  data-testid="user-menu-trigger"
                >
                  <span className="relative">
                    <Avatar userId={user.id} username={user.username} version={user.avatar_v ?? null} className="w-6 h-6 text-xs" />
                    {avatarCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-2 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-[1.1rem] text-center"
                        aria-label={gamesLabel || undefined}
                        data-testid="avatar-badge"
                      >
                        {avatarCount > 9 ? "9+" : avatarCount}
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium hidden sm:block">{user.username}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-display">
                  <div className="flex flex-col">
                    <span className="text-sm">{user.username}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.is_guest && (
                  <DropdownMenuItem onClick={() => navigate("/register")} data-testid="menu-create-account">
                    <User size={16} className="mr-2" /> Create account
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/lobby")} data-testid="menu-lobby">
                  <GameController size={16} className="mr-2" /> Lobby
                </DropdownMenuItem>
                {gamesCount > 0 && (
                  <DropdownMenuItem
                    onClick={() => {
                      navigate("/lobby#your-games");
                      setTimeout(() => document.getElementById("your-games")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
                    }}
                    data-testid="menu-your-games"
                  >
                    <Bell size={16} className="mr-2" /> Your games
                    <span className="ml-auto min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-[1.1rem] text-center" data-testid="menu-games-badge">
                      {gamesCount > 9 ? "9+" : gamesCount}
                    </span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/welcome?step=video")} data-testid="menu-tutorial">
                  <GraduationCap size={16} className="mr-2" /> Tutorial
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/daily/archive")} data-testid="menu-daily">
                  <PuzzlePiece size={16} className="mr-2" /> Daily puzzle
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/watch")} data-testid="menu-watch">
                  <Eye size={16} className="mr-2" /> Watch games
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User size={16} className="mr-2" /> Profile
                </DropdownMenuItem>
                {!user.is_guest && (<>
                <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                  <ChatCircleDots size={16} className="mr-2" /> Messages
                  {friendBadge.unread > 0 && (
                    <span className="ml-auto min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-[1.1rem] text-center" data-testid="menu-messages-badge">
                      {friendBadge.unread > 9 ? "9+" : friendBadge.unread}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile#friends")} data-testid="menu-friends">
                  <UsersThree size={16} className="mr-2" /> Friends
                  {friendBadge.requests > 0 && (
                    <span className="ml-auto min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-[1.1rem] text-center" data-testid="menu-friends-badge">
                      {friendBadge.requests > 9 ? "9+" : friendBadge.requests}
                    </span>
                  )}
                </DropdownMenuItem>
                </>)}
                <DropdownMenuItem onClick={() => setSettingsOpen(true)} data-testid="menu-settings">
                  <GearSix size={16} className="mr-2" /> Settings
                </DropdownMenuItem>
                <GamesMenu navigate={navigate} maze={!!user.maze_test} squads={!!user.squads_test} squads2={!!user.squads2_test} det={!!user.det_test} letterheist={!!user.letterheist_test} campaign={!!user.campaign_test} raidaverse={!!user.raidaverse_test} />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setFeedbackOpen(true)} data-testid="menu-feedback">
                  <ChatCircleText size={16} className="mr-2" /> Send feedback
                </DropdownMenuItem>
                {user.feedback_reader && (
                  <DropdownMenuItem onClick={() => navigate("/feedback")} data-testid="menu-read-feedback">
                    <Tray size={16} className="mr-2" /> Read feedback
                  </DropdownMenuItem>
                )}
                {user.feedback_reader && (
                  <DropdownMenuItem onClick={() => navigate("/stats")} data-testid="menu-game-stats">
                    <ChartBar size={16} className="mr-2" /> Game stats
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/privacy")} data-testid="menu-privacy">
                  <ShieldCheck size={16} className="mr-2" /> Privacy &amp; terms
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    // A guest account can't be signed back in to.
                    if (user.is_guest && !window.confirm("Signing out ends this guest account and its games. Create an account first to keep them. Sign out anyway?")) return;
                    await logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                >
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="rounded-full"
                data-testid="header-login-btn"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate("/register")}
                className="btn-tile rounded-xl px-4 bg-brand hover:bg-brand-hover text-brand-foreground"
                data-testid="header-register-btn"
              >
                Start playing
              </Button>
            </div>
          )}
        </div>
        </div>
      </div>
      {user && <SendFeedback open={feedbackOpen} onOpenChange={setFeedbackOpen} />}
      {user && <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </header>
  );
}
